import React from 'react';
import { ConfigProvider } from 'antd';

import EntryPoint from '@mindtickle/ui-shell-core/EntryPoint';
import { Provider as LoggerProvider } from '@mindtickle/ui-shell-core/Logger';
import OnIdle from '@mindtickle/ui-shell-core/IdleUntilUrgent';

import {
  DYNAMIC_REMOTES,
  NAMESPACE,
  ROUTES_NAMESPACE,
  SHAREABLE_ROUTE_PREFIX_PATH,
  FRAGMENT_COMPONENT as Fragment,
  theme,
} from '~/config/app.constants';

import NamespacedRouting from '~/components/Routing/NamespacedRouting';
import ShareableRoutes from '~/components/Routes/components/ShareableRoutes';
import { EnvProvider } from '~/components/EnvProvider';
import { DatadogProvider } from '@mindtickle/ui-shell-core/DatadogProvider';
import { TRoutingProps } from '~/components/Routing';
import InitializeDatadog from '~/components/Datadog/components/InitializeDatadog';
import { ScheduledLoadRemote } from '@mindtickle/ui-shell-core/DynamicRemote';
import { DELAYS } from '@mindtickle/ui-shell-core/tti';

import ReorgRedirection from '~/components/ReorgRedirection';
import LegacyRedirection from '~/components/LegacyAppRouting/Redirection';

import Instrumentation from './Instrumentation';
import Routing from '~/components/Routing';
import SessionExpireListener from '~/components/SessionExpireListener';
import { ShellGlobalStyle } from './style';
import { isShareableRoute } from '~/utils';

import { ThemeProvider } from 'styled-components';
// @ts-ignore
import { SidebarProvider } from '@mindtickle/ui-shell-core/Sidebar';
import useCompanySettingConfig from '~/hooks/useCompanySettingConfig';
import { CopilotProvider } from '@mindtickle/ui-shell-core/Copilot';

export type TApplicationProps = {} & TRoutingProps;

const Application = (props: TApplicationProps) => {
  const { auth, disableDatadog } = useCompanySettingConfig({
    enableDatadog: props.enableDatadog,
  });

  return (
    <LoggerProvider namespace={NAMESPACE}>
      <EnvProvider>
        <DatadogProvider>
          <ThemeProvider theme={theme}>
            <ConfigProvider prefixCls={theme.prefixCls}>
              <EntryPoint locale={auth?.user.locale}>
                <CopilotProvider>
                  <SidebarProvider>
                    <React.Fragment>
                      <ShellGlobalStyle featureToggles={auth?.featureToggles} />
                      <ReorgRedirection />
                      <LegacyRedirection />
                      <InitializeDatadog disableDatadog={disableDatadog} />
                      <Instrumentation enableDatadog={!disableDatadog} />
                      <NamespacedRouting namespace={ROUTES_NAMESPACE}>
                        <Routing {...props} />
                      </NamespacedRouting>
                      <NamespacedRouting namespace={SHAREABLE_ROUTE_PREFIX_PATH}>
                        <ShareableRoutes />
                      </NamespacedRouting>
                      <OnIdle>
                        <SessionExpireListener />
                      </OnIdle>
                      {!isShareableRoute() ? (
                        <ScheduledLoadRemote
                          remote={DYNAMIC_REMOTES.GLOBAL_UPPY_INSTANCES}
                          loader={<Fragment />}
                          lazy={true}
                          onIdleEnabled={true}
                          initializeInstances
                          scheduleAfter={DELAYS.XL}
                          shouldFailSilently
                        />
                      ) : null}
                    </React.Fragment>
                  </SidebarProvider>
                </CopilotProvider>
              </EntryPoint>
            </ConfigProvider>
          </ThemeProvider>
        </DatadogProvider>
      </EnvProvider>
    </LoggerProvider>
  );
};

export default Application;
